/**
 * Read an HTTP status from common client error shapes (FetchError, axios-like, Nuxt createError).
 */
export function getHttpStatusFromUnknown(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const record = error as Record<string, unknown>

  if (typeof record.statusCode === 'number') {
    return record.statusCode
  }
  if (typeof record.status === 'number') {
    return record.status
  }

  const response = record.response
  if (response && typeof response === 'object') {
    const nested = response as Record<string, unknown>
    if (typeof nested.status === 'number') {
      return nested.status
    }
    if (typeof nested.statusCode === 'number') {
      return nested.statusCode
    }
  }

  const data = record.data
  if (data && typeof data === 'object') {
    const dataStatus = (data as Record<string, unknown>).statusCode
    if (typeof dataStatus === 'number') {
      return dataStatus
    }
  }

  return undefined
}

export function isHttpNotFoundError(error: unknown): boolean {
  return getHttpStatusFromUnknown(error) === 404
}

/**
 * Await a TanStack/query suspense call. Map HTTP 404 rejections to `throwNotFound`;
 * otherwise run `onUnexpectedError` (optional) and rethrow the original error.
 */
export async function awaitSuspenseMappingHttpNotFound(
  runSuspense: () => Promise<unknown>,
  throwNotFound: () => never,
  onUnexpectedError?: (error: unknown) => void
): Promise<void> {
  try {
    await runSuspense()
  } catch (error: unknown) {
    if (isHttpNotFoundError(error)) {
      throwNotFound()
    }
    onUnexpectedError?.(error)
    throw error
  }
}
